import React, { useState, useEffect, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Button, Dimmer, Loader, Message, Pagination } from 'semantic-ui-react'
import { Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from '@mui/material'
import LabelIcon from '@mui/icons-material/Label'
import Data, { Column, IDatasetDetailResponse, Row } from '../../api/Data'
import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'
import Auth from '../../utils/Auth'
import './DatasetDetail.scss'
import { GlobalContext } from '../../contexts/GlobalContext'

const DatasetDetail = () => {
  const { id } = useParams<DatasetDetailParams>()
  const history = useHistory()
  const { dispatchGlobalContext } = useContext(GlobalContext)

  // States
  const [dataset, setDataset] = useState<IDatasetDetailResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState(1)
  const rowsPerPage = 20

  const [originalColumnsState, setOriginalColumnsState] = useState<{ [key: number]: boolean }>({})
  const [modifiedColumns, setModifiedColumns] = useState<{ [key: number]: boolean }>({})
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [savingChanges, setSavingChanges] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    fetchDataset()
  }, [id, currentPage])

  const fetchDataset = async () => {
    const { teamId } = Auth.getPayLoad() || {}

    if (!teamId) {
      setError('Unable to find team ID. Please refresh the page and try again!')
      setLoading(false)
      return
    }

    try {
      const skip = (currentPage - 1) * rowsPerPage
      const { data, error } = await Data.getTeamDatasetDetail(teamId, id, skip, rowsPerPage)

      if (error || !data) {
        if (error.response && error.response.data) {
          const errorMessage = error.response.data

          if (errorMessage.includes('not authorized') || errorMessage.includes('Team not found')) {
            setError('Something went wrong with your session. Please logout and back in before trying again.')
            setCountdown(5)
          } else if (errorMessage.includes('Invalid ID format') || errorMessage.includes('Dataset not found')) {
            setError(
              'Something went wrong retrieving the dataset. Please check the "My Datasets" page and try again; we\'ll automatically redirect you there soon.'
            )
            setCountdown(5)
          } else {
            setError('An error occurred. Please try again later.')
          }
        } else {
          setError('An error occurred. Please try again later.')
        }

        setDataset(null)
        setTotalPages(0)
        return
      }

      setDataset(data)
      setTotalPages(Math.ceil(data.totalRows / rowsPerPage))

      const initialState = data.columns.reduce((acc, column) => {
        acc[column.id] = !!column.isConvertToTag
        return acc
      }, {} as { [key: number]: boolean })

      setOriginalColumnsState(initialState)
      setModifiedColumns(initialState)
      setHasChanges(false)
    } catch (error) {
      setError('An error occurred. Please try again later.')
      console.error('Failed to load dataset:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!!countdown) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0) {
      history.push('/datasets')
    }
  }, [countdown, history])

  const handleBack = () => {
    if (hasChanges) {
      dispatchGlobalContext({
        type: 'set exit without saving modal',
        payload: '/datasets',
      })
    } else {
      history.push('/datasets')
    }
  }

  const handlePageChange = (_: React.SyntheticEvent, data: any) => {
    setLoading(true)
    setCurrentPage(data.activePage)
  }

  const getValueForCell = (row: Row, columnId: number): string => {
    const value = row.values.find((v) => v.columnId === columnId)
    return value ? value.value : ''
  }

  const getKeyColumn = (): Column | undefined => {
    return dataset?.columns.find((column) => column.isKey)
  }

  const saveChanges = async () => {
    if (!hasChanges) return

    const { teamId } = Auth.getPayLoad() || {}

    if (!teamId) {
      setSaveError('Unable to find team ID. Please refresh the page and try again!')
      return
    }

    try {
      setSavingChanges(true)
      setSaveError(null)

      const changedColumns = Object.keys(modifiedColumns)
        .map(columnId => parseInt(columnId))
        .filter((columnId) => modifiedColumns[columnId] !== originalColumnsState[columnId])
        .map((columnId) => ({
          id: columnId,
          isConvertToTag: modifiedColumns[columnId],
        }))

      await Data.updateDatasetColumns(teamId, id, { columns: changedColumns })

      setHasChanges(false)
      fetchDataset()
    } catch (error) {
      console.error('Failed to save column changes:', error)
      setSaveError('An error occurred while saving changes. Please try again.')
    } finally {
      setSavingChanges(false)
    }
  }

  useEffect(() => {
    dispatchGlobalContext({
      type: 'set user has made changes',
      payload: hasChanges,
    })

    return () => {
      dispatchGlobalContext({
        type: 'set user has made changes',
        payload: false,
      })
    }
  }, [hasChanges, dispatchGlobalContext])

  const handleTagIconClick = (columnId: number) => {
    setModifiedColumns((prev) => {
      const newState = { ...prev, [columnId]: !prev[columnId] }

      const changesExist = Object.keys(newState).some(
        (id) => newState[parseInt(id)] !== originalColumnsState[parseInt(id)]
      )

      setHasChanges(changesExist)
      return newState
    })
  }

  if (error && countdown !== null) {
    return (
      <Section>
        <div>
          <Title title="My Datasets" />
          <SubSection>
            <Message negative style={{ textAlign: 'center' }}>
              <Message.Header>{error}</Message.Header>
              <p>Redirecting in {countdown} seconds...</p>
            </Message>
          </SubSection>
        </div>
      </Section>
    )
  }

  return (
    <Section>
      <>
        <Title title="My Datasets">
          <>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                compact
                secondary
                disabled={!hasChanges || savingChanges}
                loading={savingChanges}
                onClick={saveChanges}
                style={{ border: '1px solid #fff' }}
              >
                Save
              </Button>
              <Button compact onClick={handleBack}>
                Back
              </Button>
            </div>
          </>
        </Title>

        {saveError && (
          <Message negative style={{ marginBottom: '1rem' }}>
            <Message.Header>Error</Message.Header>
            <p>{saveError}</p>
          </Message>
        )}

        <SubSection hasGreyBackground>
          <>
            {loading ? (
              <Dimmer active={loading}>
                <Loader size="large">Loading dataset detail...</Loader>
              </Dimmer>
            ) : dataset ? (
              <div className="dataset-detail ui segment">
                <div className="dataset-detail__title">
                  <h2>{dataset.title}</h2>
                </div>

                <div className="dataset-detail__meta-container">
                  <div
                    className={`dataset-detail__fields-container ${
                      totalPages > 1
                        ? 'dataset-detail__fields-container--with-pagination'
                        : 'dataset-detail__fields-container--full-width'
                    }`}
                  >
                    <div className="dataset-detail__key-column">
                      <div className="dataset-detail__key-column-label">Key Column</div>
                      <TextField
                        value={getKeyColumn()?.name || 'No key defined'}
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </div>

                    {dataset.description && (
                      <div className="dataset-detail__description">
                        <div className="dataset-detail__description-label">Description</div>
                        <div className="dataset-detail__description-content">{dataset.description}</div>
                      </div>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="dataset-detail__pagination">
                      <Pagination
                        activePage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        size="mini"
                        firstItem={null}
                        lastItem={null}
                        prevItem={{ content: '<', icon: true }}
                        nextItem={{ content: '>', icon: true }}
                      />
                    </div>
                  )}
                </div>
                <div className="dataset-detail__table">
                  <Table size="small">
                    <TableHead>
                      <TableRow className="dataset-detail__table-header">
                        {dataset.columns
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((column: Column) => (
                            <TableCell key={column.id} className="dataset-detail__table-header-cell">
                              <div className="dataset-detail__column-header">
                                <span className="dataset-detail__column-name">{column.name}</span>
                                <Tooltip
                                  title={
                                    modifiedColumns[column.id]
                                      ? 'Exclude as tag when linked'
                                      : 'Include as tag when linked'
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <span
                                    className={`dataset-detail__tag-icon ${
                                      modifiedColumns[column.id] ? 'dataset-detail__tag-icon--active' : ''
                                    }`}
                                    onClick={() => handleTagIconClick(column.id)}
                                  >
                                    <LabelIcon fontSize="large" />
                                  </span>
                                </Tooltip>
                              </div>
                            </TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataset.rows.map((row: Row) => (
                        <TableRow key={row.rowNumber}>
                          {dataset.columns
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((column: Column) => (
                              <TableCell key={column.id} className="dataset-detail__table-cell">
                                {getValueForCell(row, column.id)}
                              </TableCell>
                            ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <Message info className="dataset-detail__info-message dataset-message">
                <Message.Content>Dataset not found</Message.Content>
              </Message>
            )}
          </>
        </SubSection>
      </>
    </Section>
  )
}

interface DatasetDetailParams {
  id: string
}

export default DatasetDetail
